import { LifecycleStatuses, RequestCandidateDecisionEnum, RequestPriorityBoost, SortingValue } from '@constants';
import {
  ListItemsResponse,
  RequestCandidateItemResponse,
  RequestCandidatesAttachResponse,
  RequestFilterValuesResponse,
  RequestItemResponse,
  RequestLoggerItemResponse,
  RequestPayload,
  RequestSearchBody,
  RequestShortItemResponse,
} from '@innowise-group/core';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { $api } from '../axios/axios';
import qs from 'qs';

const REQUESTS_API = '/recruiting-tool/api/v1/vacancy-claims';

export default class RequestsService {
  static async createRequest(data: RequestPayload): Promise<AxiosResponse<RequestItemResponse>> {
    return $api.post<RequestItemResponse>(REQUESTS_API, data);
  }

  static async updateRequest(
    data: RequestPayload,
    id: number,
    config?: Pick<AxiosRequestConfig, 'ignoreGlobalCatch'>,
  ): Promise<AxiosResponse<RequestItemResponse>> {
    return $api.put<RequestItemResponse>(`${REQUESTS_API}/${id}`, data, config);
  }

  // Pages start from 1 in the request, while `number` of the response page starts from 0
  static async getAllRequests(args: {
    params: { currentPage: number; pageSize: number; sort: SortingValue };
    body?: RequestSearchBody;
  }): Promise<AxiosResponse<ListItemsResponse<RequestItemResponse>>> {
    const { body, params } = args;
    const sortItems = params.sort.split('%');
    const newData = {
      page: params.currentPage,
      size: params.pageSize,
      sort: sortItems,
    };
    return $api.post<ListItemsResponse<RequestItemResponse>>(
      `${REQUESTS_API}/search`,
      { ...body },
      { params: newData, paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }) },
    );
  }

  static async getVacancyRequestById(id: number): Promise<AxiosResponse<RequestItemResponse>> {
    return $api.get<RequestItemResponse>(`${REQUESTS_API}/${id}`);
  }

  static async deleteRequestById(
    id: number,
    config?: Pick<AxiosRequestConfig, 'ignoreGlobalCatch'>,
  ): Promise<AxiosResponse> {
    return $api.delete(`${REQUESTS_API}/${id}`, config);
  }

  // A full replacement: an empty list unassigns everybody
  static async updateExecutors(id: number, executorIds: number[]): Promise<AxiosResponse<RequestItemResponse>> {
    return $api.put<RequestItemResponse>(`${REQUESTS_API}/${id}/executors`, { executorIds });
  }

  // The value is absolute: the current boost ± 1 raises or lowers it, 0 resets it
  static async updatePriorityBoost(
    id: number,
    value: RequestPriorityBoost,
  ): Promise<AxiosResponse<RequestItemResponse>> {
    return $api.put<RequestItemResponse>(`${REQUESTS_API}/${id}/priority-boost`, null, { params: { value } });
  }

  static async getFilterValues(filterName: string): Promise<AxiosResponse<RequestFilterValuesResponse>> {
    return $api.get<RequestFilterValuesResponse>(`${REQUESTS_API}/values-for-filter`, { params: { filterName } });
  }

  // The answer is 200 while the request exists, the candidates that were not attached come in `rejected`
  static async attachCandidates(
    id: number,
    candidateIds: number[],
  ): Promise<AxiosResponse<RequestCandidatesAttachResponse>> {
    return $api.post<RequestCandidatesAttachResponse>(`${REQUESTS_API}/${id}/candidates`, { candidateIds });
  }

  // The latest attached first, the detached candidates are not returned
  static async getRequestCandidates(id: number): Promise<AxiosResponse<RequestCandidateItemResponse[]>> {
    return $api.get<RequestCandidateItemResponse[]>(`${REQUESTS_API}/${id}/candidates`);
  }

  static async updateCandidateDecision(
    id: number,
    candidateId: number,
    value: RequestCandidateDecisionEnum,
  ): Promise<AxiosResponse<RequestCandidateItemResponse>> {
    return $api.put<RequestCandidateItemResponse>(`${REQUESTS_API}/${id}/candidates/${candidateId}/decision`, null, {
      params: { value },
    });
  }

  static async detachCandidate(id: number, candidateId: number): Promise<AxiosResponse> {
    return $api.delete(`${REQUESTS_API}/${id}/candidates/${candidateId}`);
  }

  // At most one request today, the latest first: the array is kept for many requests per candidate
  static async getCandidateRequests(candidateId: number): Promise<AxiosResponse<RequestShortItemResponse[]>> {
    return $api.get<RequestShortItemResponse[]>(`/recruiting-tool/api/v1/candidates/${candidateId}/vacancy-claims`);
  }

  // TODO(ITSRT2-1395): manual closing is not in the request 2.0 API yet, the endpoint is provisional
  static async closeRequest(id: number): Promise<AxiosResponse> {
    return $api.post(`${REQUESTS_API}/${id}/close`);
  }

  // TODO(ITSRT2-1395): the endpoints below belong to the removed request API, the backend reworks them
  static async getVacancyRequests(
    id: number,
    page: number,
  ): Promise<AxiosResponse<{ content: RequestItemResponse[]; totalPages: number }>> {
    return $api.post<{ content: RequestItemResponse[]; totalPages: number }>(
      `/recruiting-tool/api/v1/vacancy/requests/search`,
      {
        vacancyIds: [id],
        lifecycleStatus: LifecycleStatuses.Actual,
      },
      {
        params: {
          page,
        },
      },
    );
  }

  static async requestsExport(body: object): Promise<AxiosResponse<Blob>> {
    return $api.post(
      '/recruiting-tool/api/v1/vacancy/requests/export',
      { ...body },
      {
        responseType: 'blob',
      },
    );
  }

  // Called for every candidate of the archive page, so a failure must not raise a toast per candidate
  static async checkCandidateHistory(candidateId: number): Promise<AxiosResponse<boolean>> {
    return $api.get(`/recruiting-tool/api/v1/vacancy/requests/story/check`, {
      params: { candidateId },
      ignoreGlobalCatch: true,
    });
  }

  static async getHistoryByRequest(
    requestId: number,
    params: { page: number; size: number },
  ): Promise<AxiosResponse<ListItemsResponse<RequestLoggerItemResponse>>> {
    return $api.get(`/recruiting-tool/api/v1/vacancy/requests/story/${requestId}`, { params });
  }
}
